import React, { useEffect, useRef, useState } from 'react';
import {
  METRIC_FILTER_OPTIONS, countActiveMetricFilters, metricScale,
  type MetricFilterKey, type MetricFilters, type MetricRange, type MetricUnit,
} from './clientViewTokens';

interface MetricFiltersPopoverProps {
  filters: MetricFilters;
  onChange: (filters: MetricFilters) => void;
  disabled?: boolean;
}

const UNIT_SUFFIX: Record<MetricUnit, string> = {
  count: '', percent: '%', miles: 'mi', days: 'd',
};

/** Parse a range input back to the stored scale (percent inputs are ratios). */
function parseBound(raw: string, unit: MetricUnit): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return n / metricScale(unit);
}

/** Render a stored bound in the unit the user types in. */
function formatBound(v: number | null | undefined, unit: MetricUnit): string {
  if (v === null || v === undefined) return '';
  const scaled = v * metricScale(unit);
  return String(Math.round(scaled * 100) / 100);
}

/**
 * Min/max ranges for every numeric card metric, in one popover rather than a
 * dozen chip rows. Edits stay in a local draft until Apply, so typing "1", "12",
 * "125" doesn't re-filter (and re-page) the board on every keystroke. Applied to
 * the whole board — the thin map points carry these metrics — so a range narrows
 * the dealer count, not just the visible page.
 */
export const MetricFiltersPopover: React.FC<MetricFiltersPopoverProps> = ({
  filters, onChange, disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<MetricFilters>(filters);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const activeCount = countActiveMetricFilters(filters);
  const dirty = JSON.stringify(draft) !== JSON.stringify(filters);

  // Reopening (or an outside change, e.g. applying a saved view) restarts the
  // draft from what is actually filtering the board.
  useEffect(() => { setDraft(filters); }, [filters, open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const setBound = (key: MetricFilterKey, unit: MetricUnit, side: keyof MetricRange, raw: string) => {
    setDraft((prev) => {
      const current = prev[key] ?? { min: null, max: null };
      const next: MetricRange = { ...current, [side]: parseBound(raw, unit) };
      const cleaned: MetricFilters = { ...prev };
      if (next.min === null && next.max === null) delete cleaned[key];
      else cleaned[key] = next;
      return cleaned;
    });
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <div className="cv-metric-filters" ref={wrapRef}>
      <button
        type="button"
        className={`cv-metric-filters-btn${activeCount > 0 ? ' active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title="Filter the carousel by metric ranges"
      >
        Metrics{activeCount > 0 ? ` (${activeCount})` : ''} ▾
      </button>
      {open && (
        <div className="cv-metric-filters-menu" role="dialog" aria-label="Metric range filters">
          <div className="cv-metric-filters-head">
            <span>Min / max</span>
            {countActiveMetricFilters(draft) > 0 && (
              <button type="button" className="cv-metric-filters-clear" onClick={() => setDraft({})}>
                Clear all
              </button>
            )}
          </div>
          <div className="cv-metric-filters-rows">
            {METRIC_FILTER_OPTIONS.map((opt) => {
              const range = draft[opt.key];
              return (
                <div key={opt.key} className="cv-metric-filters-row">
                  <span className="cv-metric-filters-name">{opt.label}</span>
                  <input
                    type="number"
                    className="cv-metric-filters-input"
                    placeholder="min"
                    aria-label={`${opt.label} minimum`}
                    value={formatBound(range?.min, opt.unit)}
                    onChange={(e) => setBound(opt.key, opt.unit, 'min', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
                  />
                  <input
                    type="number"
                    className="cv-metric-filters-input"
                    placeholder="max"
                    aria-label={`${opt.label} maximum`}
                    value={formatBound(range?.max, opt.unit)}
                    onChange={(e) => setBound(opt.key, opt.unit, 'max', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
                  />
                  <span className="cv-metric-filters-unit">{UNIT_SUFFIX[opt.unit]}</span>
                </div>
              );
            })}
          </div>
          <div className="cv-metric-filters-foot">
            <button type="button" className="cv-reset-btn" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="cv-metric-filters-apply" onClick={apply} disabled={!dirty}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
